import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Lightbox from 'yet-another-react-lightbox'
import 'yet-another-react-lightbox/styles.css'
import { Flame, Wind, Droplets } from 'lucide-react'
import { Container } from '@/components/layout'
import { SEO } from '@/components/SEO'
import { cn } from '@/lib/utils'
import type { CertificateCategory } from '@/types'

const categories: { id: CertificateCategory | 'all'; name: string; icon?: React.ComponentType<{ className?: string }> }[] = [
  { id: 'all', name: 'All Certificates' },
  { id: 'fire', name: 'Fire', icon: Flame },
  { id: 'air', name: 'Air', icon: Wind },
  { id: 'water', name: 'Water', icon: Droplets },
]

// Temporary mock data - will be replaced with Supabase data
const mockCertificates = [
  { id: '1', name: 'DNV GL Fire Certificate', category: 'fire' as CertificateCategory, image: 'https://picsum.photos/seed/cert1/400/500' },
  { id: '2', name: 'Bureau Veritas Fire Rating', category: 'fire' as CertificateCategory, image: 'https://picsum.photos/seed/cert2/400/500' },
  { id: '3', name: 'Lloyd\'s Watertight Approval', category: 'water' as CertificateCategory, image: 'https://picsum.photos/seed/cert3/400/500' },
  { id: '4', name: 'RINA Watertight Certificate', category: 'water' as CertificateCategory, image: 'https://picsum.photos/seed/cert4/400/500' },
  { id: '5', name: 'ABS Air Tightness', category: 'air' as CertificateCategory, image: 'https://picsum.photos/seed/cert5/400/500' },
  { id: '6', name: 'DNV Airtight Doors', category: 'air' as CertificateCategory, image: 'https://picsum.photos/seed/cert6/400/500' },
  { id: '7', name: 'A60 Fire Rating', category: 'fire' as CertificateCategory, image: 'https://picsum.photos/seed/cert7/400/500' },
  { id: '8', name: 'Weathertight Certification', category: 'water' as CertificateCategory, image: 'https://picsum.photos/seed/cert8/400/500' },
]

export function CertificatesPage() {
  const [activeCategory, setActiveCategory] = useState<CertificateCategory | 'all'>('all')
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  const filteredCertificates =
    activeCategory === 'all'
      ? mockCertificates
      : mockCertificates.filter((cert) => cert.category === activeCategory)

  const openLightbox = (index: number) => {
    setLightboxIndex(index)
    setLightboxOpen(true)
  }

  const getCategoryColor = (category: CertificateCategory) => {
    switch (category) {
      case 'fire':
        return 'bg-red-100 text-red-700'
      case 'air':
        return 'bg-sky-100 text-sky-700'
      case 'water':
        return 'bg-blue-100 text-blue-700'
    }
  }

  return (
    <>
      <SEO pageKey="certificates" fallback={{ title: 'Quality Certifications - EngiMarineX Standards & Compliance' }} />
      {/* Hero */}
      <section className="bg-secondary-900 text-white py-16 lg:py-24">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">Certificates</h1>
            <p className="text-lg text-secondary-300 leading-relaxed">
              Our products are certified by leading classification societies
              worldwide. View our certifications for fire resistance, air
              tightness, and water tightness.
            </p>
          </motion.div>
        </Container>
      </section>

      {/* Certificates */}
      <section className="py-16 lg:py-24">
        <Container>
          {/* Category Filters */}
          <div className="flex flex-wrap gap-3 mb-10">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={cn(
                  'flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all',
                  activeCategory === cat.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
                )}
              >
                {cat.icon && <cat.icon className="w-4 h-4" />}
                {cat.name}
              </button>
            ))}
          </div>

          {/* Certificates Grid */}
          <motion.div
            layout
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
          >
            <AnimatePresence mode="popLayout">
              {filteredCertificates.map((cert, index) => (
                <motion.div
                  key={cert.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                  className="group cursor-pointer"
                  onClick={() => openLightbox(index)}
                >
                  <div className="card-hover">
                    <div className="aspect-[3/4] bg-white overflow-hidden">
                      <img
                        src={cert.image}
                        alt={cert.name}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4">
                      <span className={cn('badge text-xs mb-2', getCategoryColor(cert.category))}>
                        {cert.category.charAt(0).toUpperCase() + cert.category.slice(1)}
                      </span>
                      <h3 className="font-semibold text-secondary-900 group-hover:text-primary-600 transition-colors">
                        {cert.name}
                      </h3>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>

          {filteredCertificates.length === 0 && (
            <div className="text-center py-16">
              <p className="text-secondary-500">No certificates found for this category.</p>
            </div>
          )}
        </Container>
      </section>

      {/* Lightbox */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={filteredCertificates.map((cert) => ({ src: cert.image, alt: cert.name }))}
      />
    </>
  )
}
